import { useEffect, useRef } from 'react';
import { Box, Spinner } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';

interface Props {
  bgcolor?: string;
  size?: string;
  py?: number;
  context?: string; // Component or page name where loader is used
  timeoutMs?: number; // Time in ms before triggering Sentry alert (default: 10s)
}

const CoverLoader = ({
  bgcolor = 'rgba(255, 255, 255, 0.7)',
  size,
  py,
  context = 'Unknown',
  timeoutMs = 10000,
}: Props): JSX.Element => {
  const mountTimeRef = useRef<number>(Date.now());
  const sentryReportedRef = useRef<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Only report once per mount
      if (!sentryReportedRef.current) {
        sentryReportedRef.current = true;
        const loadingDuration = Date.now() - mountTimeRef.current;

        Sentry.captureException(new Error('CoverLoader timeout - possible infinite loading'), {
          tags: {
            component: 'CoverLoader',
            context,
            loadingDuration: `${loadingDuration}ms`,
          },
          extra: {
            context,
            loadingDuration,
            timeoutThreshold: timeoutMs,
            mountTime: new Date(mountTimeRef.current).toISOString(),
            url: typeof window !== 'undefined' ? window.location.href : 'N/A',
          },
        });
      }
    }, timeoutMs);

    return () => {
      clearTimeout(timer);
    };
  }, [context, timeoutMs]);

  return (
    <Box
      py={py || 0}
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      justifyContent="center"
      alignItems="center"
      backgroundColor={bgcolor}
    >
      <Spinner size={size} />
    </Box>
  );
};

export default CoverLoader;
